import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuEditorComponent } from '../../components/menu-editor/menu-editor.component';
import { NotificationComponent } from '../../components/notification/notification.component';
import { WebsiteService, SupportedLanguage, LanguageOption } from '../../services/website.service';
import { NotificationService } from '../../services/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-menu-page',
  standalone: true,
  imports: [
    CommonModule,
    MenuEditorComponent,
    NotificationComponent
  ],
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuPageComponent implements OnInit, OnDestroy {
  currentDomain: string = '';
  currentLocale: SupportedLanguage = 'en';
  availableLanguages: LanguageOption[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private websiteService: WebsiteService,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    // Subscribe to domain changes
    this.websiteService.currentDomain$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async domain => {
        this.currentDomain = domain || '';
        if (this.currentDomain) {
          await this.loadWebsiteSettings();
        }
      });

    // Subscribe to language changes
    this.websiteService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currentLocale = lang;
      });

    // Get initial domain and load settings
    this.currentDomain = this.websiteService.getCurrentDomain() || '';
    if (this.currentDomain) {
      await this.loadWebsiteSettings();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadWebsiteSettings() {
    if (!this.currentDomain) return;
    
    try {
      const settings = await this.websiteService.getWebsiteSettings(this.currentDomain);
      if (settings) {
        this.availableLanguages = settings.availableLanguages || [];
        // Alleen de default taal instellen als de opgeslagen taal niet beschikbaar is
        const availableCodes = this.availableLanguages.map(l => l.code);
        const storedLang = this.websiteService.getCurrentLanguage();
        if (!availableCodes.includes(storedLang) && settings.defaultLanguage) {
          const defaultLang = settings.defaultLanguage as SupportedLanguage;
          this.currentLocale = defaultLang;
          this.websiteService.setCurrentLanguage(defaultLang);
        }
      }
    } catch (error) {
      console.error('Error loading website settings:', error);
    }
  }

  setCurrentLanguage(code: string) {
    this.currentLocale = code as SupportedLanguage;
    this.websiteService.setCurrentLanguage(this.currentLocale);
    
    // Get language name for feedback
    const language = this.availableLanguages.find(lang => lang.code === code);
    const languageName = language?.name || code.toUpperCase();
    
    this.notificationService.show({
      message: `Taal gewijzigd naar ${languageName}`,
      type: 'success'
    });
  }

  onMenuChanged() {
    // Menu was changed, could trigger a refresh if needed
  }
}
